import { isNoSpeechWarningMidSessionEnabled } from '@/app-shell/flags/no-speech-mid-session-warning-flag';
import { isNoSpeechWarningStartupEnabled } from '@/app-shell/flags/no-speech-startup-warning-flag';
import {
  tauriSessionMismatch,
  trackGetCurrentRecordingFailed,
} from '@/lib/analytics/scribe/general';
import { uploadV2Chunk } from '@/lib/SessionRecordingProvider/t11nClient';
import { UploadStatus } from '@/models/tauriRecordModel';
import { type Dispatch, store } from '@/store';
import { recorderFmc } from '@/tauri/controller/recording';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { chunkDurationSecsFromSize } from '@heidi/capture/chunkDuration';
import { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from '@heidi/session/contracts';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { pollLiveTranscripts } from '../query/useQueryLiveTranscriptBySessionId';
import { useRecordingSessionId } from '../useCurrentIds';
import {
  coordinateLegacyChunkUpload,
  type LegacyChunkUploadIntent,
} from './legacyChunkUploadCoordinator';

const GET_AND_UPLOAD_CHUNK_RECORDING = 'getAndUploadChunkRecording';

const CHUNK_OUTCOME_EVENT = 'DESKTOP_CAPTURE_INTEGRITY_CHUNK_OUTCOME';

/**
 * DSK-795: emit one PHI-free structured log per terminal chunk outcome on the
 * legacy frontend upload path, so a missing-audio report (DSK-720) can be
 * reconstructed from production logs alone — which zero-based chunk indexes
 * were uploaded, which were deliberately skipped, and which were lost — without
 * a staged Windows/Dialpad reproduction.
 *
 * Every field is a technical id, index, byte/ms count, or bounded enum. No
 * audio, transcript text, patient data, or raw error bodies cross this boundary.
 * Join across a recording with `evidence_join_id` (the technical session id).
 *
 * The payload is bound with `logger.child(...)` rather than passed as a second
 * `logger.info` argument, because only child bindings are merged into the
 * context the Datadog sink receives — a second argument is bucketed into
 * `additionalArgs`, landing the fields at `@additionalArgs[0].chunk_index`,
 * which facets and group-bys handle poorly. Passing the payload as the message
 * object instead is worse again: pino is `level: 'silent'` in prod, so Datadog
 * is the only sink there, and it stringifies an object message into the log
 * text with no attributes at all. Binding keeps `@chunk_index` top-level and
 * the event name searchable, matching how the Rust siblings reach Datadog
 * (`tauriRustLogs.ts` binds their parsed `key=value` fields the same way).
 */
function logChunkOutcome({
  sessionId,
  recordingId,
  chunkIndex,
  stage,
  outcome,
  reason,
  sizeBytes,
  durationMs,
}: {
  sessionId: string;
  recordingId?: string;
  chunkIndex: number;
  stage: 'extraction' | 'upload';
  outcome: 'completed' | 'intentionally_skipped' | 'failed';
  reason?:
    | 'recording_unavailable'
    | 'recording_id_missing'
    | 'below_minimum_size'
    | 'dropped_too_small'
    | 'stale_session'
    | 'rejected'
    | 'request_failed';
  sizeBytes?: number;
  durationMs?: number;
}) {
  logger
    .child({
      // Same value as `session_id` today: the fixed join field name, kept
      // alongside the one the Rust siblings already key on.
      evidence_join_id: sessionId,
      session_id: sessionId,
      recording_id: recordingId,
      capture_pipeline: 'legacy-fe',
      chunk_index: chunkIndex,
      stage,
      outcome,
      reason,
      size_bytes: sizeBytes,
      duration_ms: durationMs,
    })
    .info(CHUNK_OUTCOME_EVENT);
}

export function useMutateGetAndUploadChunkRecording() {
  // const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();
  const { mutateAsync: uploadChunkRecording } = useMutateUploadChunkRecording();
  // Use the recording session (agent override off-route) not just the route
  // session — otherwise an agent-driven recording on the projects route
  // (route session = "") mismatches its own chunks and skips every upload,
  // leaving the recording with no transcript.
  const sessionIdFromRoute = useRecordingSessionId();

  return useMutation({
    mutationKey: [GET_AND_UPLOAD_CHUNK_RECORDING],
    networkMode: isTauri() ? 'always' : 'offlineFirst',
    mutationFn: async (input: {
      sessionId: string;
      intent?: LegacyChunkUploadIntent;
    }) => {
      const { sessionId, intent = 'periodic' } = input;
      return coordinateLegacyChunkUpload({
        sessionId,
        intent,
        run: async () => {
          if (sessionIdFromRoute !== sessionId) {
            logger.debug(
              'mismatch in session id: session id from route',
              sessionIdFromRoute,
              'session id from input',
              sessionId
            );
            tauriSessionMismatch();
            return;
          }
          const initialChunks = store.getState().tauriRecordModel.chunks;

          // Both this and the route-session mismatch above return BEFORE
          // `get_current_recording`, so no audio has been extracted and none is
          // lost — deliberately uninstrumented. The fence below is the one that
          // discards already-extracted audio.
          if (initialChunks.session_id !== sessionId) {
            logger.debug(
              'Skipping chunk upload due to session mismatch. Active session:',
              initialChunks.session_id,
              'upload request session:',
              sessionId
            );
            tauriSessionMismatch();
            return;
          }

          const recordingId = initialChunks.recording_id;
          const recording = await recorderFmc.get_current_recording();
          const { chunks } = store.getState().tauriRecordModel;

          // Extraction runs outside the recorder lock. A stop/restart can replace
          // Redux state while it is in flight, so fence the result before append.
          if (chunks.session_id !== sessionId || chunks.recording_id !== recordingId) {
            logger.debug('Skipping stale chunk result after session changed');
            const lostSizeBytes = recording?.size;
            // This branch loses audio, unlike the two session-mismatch returns
            // above: `get_current_recording` has already run, and
            // `prepare_chunk_v2` advances the recorder's read cursor by the
            // window it extracted, so the audio in `recording` is never handed
            // out again. Nothing else records the loss either — `current_index`
            // does not advance here, so the chunk-index contiguity check reads a
            // session that dropped audio as a clean, fully-contiguous run.
            // `initialChunks.current_index` is the index the lost chunk would
            // have taken; `chunks.current_index` now belongs to the replacement
            // session.
            logChunkOutcome({
              sessionId,
              recordingId,
              chunkIndex: initialChunks.current_index,
              stage: 'extraction',
              outcome: 'failed',
              reason: 'stale_session',
              sizeBytes: lostSizeBytes,
              durationMs:
                lostSizeBytes === undefined
                  ? undefined
                  : chunkDurationSecsFromSize(lostSizeBytes) * 1000,
            });
            return;
          }

          const chunkIndex = chunks.current_index;
          const nextIndex = chunkIndex + 1;
          if (recording?.size === 0 && chunkIndex === 0) {
            const currentAudioFile = await recorderFmc.inspect_audio_file();
            logger.debug('currentAudioFile', currentAudioFile?.size);
            const currentSpeakerFile = await recorderFmc.inspect_speaker_file();
            logger.debug('currentSpeakerFile', currentSpeakerFile?.size);
            trackGetCurrentRecordingFailed({
              speakerSize: currentSpeakerFile?.size,
              audioSize: currentAudioFile?.size,
            });
          }
          if (!recording) {
            logger.debug('recording is null');
            logChunkOutcome({
              sessionId,
              recordingId,
              chunkIndex,
              stage: 'extraction',
              outcome: 'failed',
              reason: 'recording_unavailable',
            });
            dispatch.tauriRecordModel.setChunks({
              current_index: nextIndex,
              blobs: [
                ...chunks.blobs,
                {
                  chunk: [],
                  status: UploadStatus.Failed,
                  duration: 0,
                  size: 0,
                  name: '',
                },
              ],
              session_id: sessionId,
              recording_id: recordingId,
            });
            return;
          }

          // APP-7965: report the true audio length of THIS chunk, derived from its
          // CBR MP3 byte size — never the nominal chunk interval.
          const chunkDurationSecs = chunkDurationSecsFromSize(recording.size);
          const byteArray = new Uint8Array(recording.bytes);
          const blob = new Blob([byteArray], { type: 'audio/mp3' });
          dispatch.tauriRecordModel.setChunks({
            current_index: nextIndex,
            blobs: [
              ...chunks.blobs,
              {
                chunk: [],
                status: UploadStatus.Queued,
                duration: chunkDurationSecs,
                size: recording.size,
                name: recording.name,
              },
            ],
            session_id: sessionId,
            recording_id: recordingId,
          });

          if (!recordingId) {
            dispatch.tauriRecordModel.editChunkStatus({
              sessionId,
              index: chunkIndex,
              status: UploadStatus.Failed,
            });
            logChunkOutcome({
              sessionId,
              chunkIndex,
              stage: 'upload',
              outcome: 'failed',
              reason: 'recording_id_missing',
              sizeBytes: recording.size,
              durationMs: chunkDurationSecs * 1000,
            });
            alert('ID not found. Please end recording and start again');
            return;
          }
          if (recording.size < 1042) {
            logger.debug('recording too small, marking as not sending', recording.size);
            dispatch.tauriRecordModel.editChunkStatus({
              sessionId,
              index: chunkIndex,
              status: UploadStatus.Completed,
            });
            logChunkOutcome({
              sessionId,
              recordingId,
              chunkIndex,
              stage: 'extraction',
              outcome: 'intentionally_skipped',
              reason: 'below_minimum_size',
              sizeBytes: recording.size,
              durationMs: chunkDurationSecs * 1000,
            });
            return;
          }

          await uploadChunkRecording({
            sessionId,
            recordingId,
            index: chunkIndex,
            chunk: blob,
            duration: chunkDurationSecs,
            path: recording.path,
          });
        },
      });
    },
    // onSuccess: (_, input) => {},
    onError: (error) => {
      logger.debug('Error in get and upload chunk recording: ', error);
    },
  });
}

export const UPLOAD_CHUNK_RECORDING = 'uploadChunkRecording';

export function useMutateUploadChunkRecording() {
  // const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();
  return useMutation({
    mutationKey: [UPLOAD_CHUNK_RECORDING],
    networkMode: isTauri() ? 'always' : 'offlineFirst',
    retry: 5,
    mutationFn: async (input: {
      sessionId: string;
      recordingId: string;
      index: number;
      chunk: Blob;
      duration: number;
      path?: string;
    }) => {
      const { sessionId, recordingId, index, chunk, duration, path } = input;
      // APP-3779: read the locked version from Redux (NOT the live flag) so
      // mid-session flag flips can't corrupt the upload pipeline. The locked
      // value was written by `useMutateStartTauriRecording` at session start
      // via `setUseTranscriptionServiceV2`. It lives at the top of the
      // tauriRecordModel state (NOT inside `chunks`) because `setChunks` is
      // a replace reducer — the previous in-`chunks` location was wiped by
      // the very first `setChunks` call from this hook's caller.
      const useV2 = Boolean(store.getState().tauriRecordModel.useTranscriptionServiceV2);
      logger.debug('uploading chunk', index, 'useV2', useV2);

      if (useV2) {
        const v2Outcome = await uploadV2Chunk({
          sessionId,
          recordingId,
          blob: chunk,
          index,
          // v2 expects duration in milliseconds; the desktop chunk interval
          // is tracked in seconds in `getChunkRecordingAndUpload`.
          durationMs: duration * 1000,
          fileName: `chunk-${index + 1}.mp3`,
        });
        // `uploadV2Chunk` resolves on both success and silent 422/FileTooSmall
        // drop — both are "done" from the desktop's perspective. Any genuine
        // failure propagates and the `onError` handler marks the chunk failed.
        dispatch.tauriRecordModel.editChunkStatus({
          sessionId,
          index,
          status: UploadStatus.Completed,
        });
        // DSK-795: a swallowed 422 is NOT a completed upload — the chunk never
        // reaches the transcript, so it must read as a gap. Recorded as `failed`
        // rather than `intentionally_skipped` because the contiguity check
        // excludes intentionally-skipped indexes, and the swallow catches ANY
        // 422 (see `uploadV2Chunk`), not only genuinely too-short audio.
        logChunkOutcome({
          sessionId,
          recordingId,
          chunkIndex: index,
          stage: 'upload',
          ...(v2Outcome === 'dropped_too_small'
            ? { outcome: 'failed' as const, reason: 'dropped_too_small' as const }
            : { outcome: 'completed' as const }),
          sizeBytes: chunk.size,
          durationMs: duration * 1000,
        });
        if (path) {
          void recorderFmc.remove_file_quietly(path);
        }
        return;
      }

      const formData = new FormData();
      formData.append('file', chunk);
      formData.append('index', String(index));
      // Fractional seconds to 3dp — the same wire format the Rust engine's v1
      // path has always sent to this endpoint (`upload_client.rs`, `{:.3}`),
      // so fractional durations are already proven against the v1 backend.
      formData.append('duration', duration.toFixed(3));
      const { data } = await scribeHttpV2.post<{ is_success: boolean }>(
        `/sessions/${sessionId}/restful-segment-transcription/${recordingId}:transcribe`,
        formData
      );

      if (data.is_success) {
        dispatch.tauriRecordModel.editChunkStatus({
          sessionId,
          index,
          status: UploadStatus.Completed,
        });
        logChunkOutcome({
          sessionId,
          recordingId,
          chunkIndex: index,
          stage: 'upload',
          outcome: 'completed',
          sizeBytes: chunk.size,
          durationMs: duration * 1000,
        });
        if (path) {
          void recorderFmc.remove_file_quietly(path);
        }
      } else {
        dispatch.tauriRecordModel.editChunkStatus({
          sessionId,
          index,
          status: UploadStatus.Failed,
        });
        logChunkOutcome({
          sessionId,
          recordingId,
          chunkIndex: index,
          stage: 'upload',
          outcome: 'failed',
          reason: 'rejected',
          sizeBytes: chunk.size,
          durationMs: duration * 1000,
        });
      }
    },
    onSuccess: (_, input) => {
      void pollLiveTranscripts(input.sessionId);
      if (
        (isNoSpeechWarningStartupEnabled() || isNoSpeechWarningMidSessionEnabled()) &&
        store.getState().tauriRecordModel.useTranscriptionServiceV2
      ) {
        void reactQueryClient.invalidateQueries({
          queryKey: [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, input.sessionId],
        });
      }
    },
    onError: (error, variables) => {
      const { index, sessionId, recordingId } = variables;
      logger.error(
        `Error in uploading chunk: sessionId: ${sessionId}, recordingId: ${recordingId}, index: ${index}`,
        error
      );
      dispatch.tauriRecordModel.editChunkStatus({
        sessionId,
        index,
        status: UploadStatus.Failed,
      });
      logChunkOutcome({
        sessionId,
        recordingId,
        chunkIndex: index,
        stage: 'upload',
        outcome: 'failed',
        reason: 'request_failed',
        sizeBytes: variables.chunk.size,
        durationMs: variables.duration * 1000,
      });
    },
  });
}
